import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { usePostHog } from 'posthog-js/react';
import { RootState } from '@/types/graph';
import { client } from '@/util/client';
import { KIOSK_INACTIVITY_RESET_EVENT } from '@/util/kiosk';

/**
 * Records the last question the patient saw, for drop-off analysis.
 *
 * Fire-and-forget analytics write: never blocks or slows navigation.
 *
 * `displayedQuestionId` must be the question the patient is being asked —
 * `questionIdForResponse`, not the raw queue entry. On an INFO pairing the
 * queue index rests on the INFO block while the question renders below it, so
 * the queue entry would name a non-question and lose the real last question.
 */
export const useSaveLastActiveQuestion = (
  displayedQuestionId: string | undefined,
  isQuestionDisplayed: boolean,
) => {
  const questionnaireId = useSelector(
    (state: RootState) => state.questionnaireId,
  );
  const displayFullQueue = useSelector(
    (state: RootState) => state.graph.options.DISPLAY_FULL_QUEUE,
  );
  const posthog = usePostHog();

  const lastSavedRef = useRef<string | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!questionnaireId) return;
    if (!isQuestionDisplayed || !displayedQuestionId) return;
    // Only the Debug panel and the scenario runner raise this: keeps debug
    // and scripted sessions out of the data.
    if (displayFullQueue) return;

    const saveKey = `${questionnaireId}:${displayedQuestionId}`;
    if (saveKey === lastSavedRef.current) return;

    // Abort the in-flight save so only the newest question is waited on. This
    // does not order the DB writes — an already-dispatched PATCH can still
    // commit late. Accepted: one stale id, corrected on the next navigation.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    client
      .patch(
        `/questionnaire/${questionnaireId}/last-active-question`,
        { lastActiveQuestionId: displayedQuestionId },
        { signal: controller.signal },
      )
      .then(() => {
        lastSavedRef.current = saveKey;
      })
      .catch(() => {
        // Swallowed: losing a data point beats disturbing the patient.
      });
  }, [
    questionnaireId,
    isQuestionDisplayed,
    displayFullQueue,
    displayedQuestionId,
  ]);

  // An event, not a session property: `$pageleave` rarely fires on mobile, and
  // a kiosk tab is never hidden, so the kiosk reset reports it explicitly.
  useEffect(() => {
    if (!isQuestionDisplayed || !displayedQuestionId || displayFullQueue) {
      return;
    }

    const report = (trigger: 'hidden' | 'kiosk_timeout') =>
      posthog?.capture(
        'patient_questionnaire_left',
        { lastActiveQuestionId: displayedQuestionId, trigger },
        { send_instantly: true },
      );
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') report('hidden');
    };
    const onKioskReset = () => report('kiosk_timeout');

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener(KIOSK_INACTIVITY_RESET_EVENT, onKioskReset);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener(KIOSK_INACTIVITY_RESET_EVENT, onKioskReset);
    };
  }, [posthog, isQuestionDisplayed, displayFullQueue, displayedQuestionId]);
};
